import React, { useState, useCallback, useMemo } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ClientMatterPicker from '../../../components/shared/ClientMatterPicker';
import type { ClientMatterSelection } from '../../../components/shared/ClientMatterPicker';
import { useBilling } from '../hooks/useBilling';
import { useUnbilledItems } from '../hooks/useUnbilledItems';
import BillableItemRow from '../components/BillableItemRow';
import InvoiceTotalsBar from '../components/InvoiceTotalsBar';
import type { LineItemOverride } from '../types/billing.types';

type ItemTab = 'time-entries' | 'expenses';

const CreateInvoicePage: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const { createBill } = useBilling();
  const unbilled = useUnbilledItems();

  // Matter selection
  const [matterId, setMatterId] = useState('');
  const [matterLabel, setMatterLabel] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [showPicker, setShowPicker] = useState(true);

  // Form fields
  const [description, setDescription] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  // Item selection + overrides
  const [selectedTimeEntryIds, setSelectedTimeEntryIds] = useState<Set<string>>(new Set());
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Map<string, LineItemOverride>>(new Map());
  const [itemTab, setItemTab] = useState<ItemTab>('time-entries');
  const [submitting, setSubmitting] = useState(false);

  const handleMatterSelect = useCallback(async (selection: ClientMatterSelection) => {
    setMatterId(selection.matterId);
    setMatterLabel(
      selection.matterNumber
        ? `${selection.matterNumber}${selection.matterName ? ` — ${selection.matterName}` : ''}`
        : selection.matterName || selection.matterId
    );
    setClientLabel(selection.clientName);
    setShowPicker(false);

    // Fetch unbilled items for this matter
    await unbilled.fetchForMatter(selection.matterId, periodStart || undefined, periodEnd || undefined);
  }, [periodStart, periodEnd, unbilled.fetchForMatter]);

  // Initialize selections when items load
  React.useEffect(() => {
    if (unbilled.timeEntries.length > 0 || unbilled.expenses.length > 0) {
      setSelectedTimeEntryIds(new Set(unbilled.timeEntries.map((te) => te.time_entry_id)));
      setSelectedExpenseIds(new Set(unbilled.expenses.map((e) => e.expense_id)));
      setOverrides(new Map());
    }
  }, [unbilled.timeEntries, unbilled.expenses]);

  // Refetch when period changes (if matter selected)
  const handlePeriodChange = useCallback((field: 'start' | 'end', val: string) => {
    const newStart = field === 'start' ? val : periodStart;
    const newEnd = field === 'end' ? val : periodEnd;
    if (field === 'start') setPeriodStart(val);
    else setPeriodEnd(val);
    if (matterId) {
      unbilled.fetchForMatter(matterId, newStart || undefined, newEnd || undefined);
    }
  }, [matterId, periodStart, periodEnd, unbilled.fetchForMatter]);

  // Toggle helpers
  const toggleTimeEntry = useCallback((id: string) => {
    setSelectedTimeEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleExpense = useCallback((id: string) => {
    setSelectedExpenseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllTimeEntries = useCallback(() => {
    setSelectedTimeEntryIds(new Set(unbilled.timeEntries.map((te) => te.time_entry_id)));
  }, [unbilled.timeEntries]);

  const deselectAllTimeEntries = useCallback(() => {
    setSelectedTimeEntryIds(new Set());
  }, []);

  const selectAllExpenses = useCallback(() => {
    setSelectedExpenseIds(new Set(unbilled.expenses.map((e) => e.expense_id)));
  }, [unbilled.expenses]);

  const deselectAllExpenses = useCallback(() => {
    setSelectedExpenseIds(new Set());
  }, []);

  // Override handler
  const handleOverrideChange = useCallback((id: string, update: Partial<LineItemOverride> | null) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      if (update === null) {
        next.delete(id);
      } else {
        const existing = next.get(id) || { id };
        next.set(id, { ...existing, ...update });
      }
      return next;
    });
  }, []);

  const allTimeSelected = selectedTimeEntryIds.size === unbilled.timeEntries.length && unbilled.timeEntries.length > 0;
  const allExpSelected = selectedExpenseIds.size === unbilled.expenses.length && unbilled.expenses.length > 0;

  const handleSubmit = async () => {
    if (!matterId || submitting) return;
    setSubmitting(true);
    try {
      const allTimeIds = new Set(unbilled.timeEntries.map((te) => te.time_entry_id));
      const allExpIds = new Set(unbilled.expenses.map((e) => e.expense_id));

      const isAllTime = selectedTimeEntryIds.size === allTimeIds.size &&
        [...selectedTimeEntryIds].every((id) => allTimeIds.has(id));
      const isAllExp = selectedExpenseIds.size === allExpIds.size &&
        [...selectedExpenseIds].every((id) => allExpIds.has(id));

      const dto: any = {
        matter_id: matterId,
        description: description || undefined,
        period_start: periodStart || undefined,
        period_end: periodEnd || undefined,
      };

      if (!isAllTime) {
        dto.selected_time_entry_ids = [...selectedTimeEntryIds];
      }
      if (!isAllExp) {
        dto.selected_expense_ids = [...selectedExpenseIds];
      }
      if (overrides.size > 0) {
        dto.line_item_overrides = [...overrides.values()];
      }

      const bill = await createBill(dto);
      if (bill?.bill_id) {
        navigate(`/billing/invoices/${bill.bill_id}`);
      } else {
        navigate('/billing');
      }
    } catch {
      // error handled by hook
    } finally {
      setSubmitting(false);
    }
  };

  const hasItems = unbilled.timeEntries.length > 0 || unbilled.expenses.length > 0;
  const hasSelection = selectedTimeEntryIds.size > 0 || selectedExpenseIds.size > 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 lex-tab-panel">
      {/* Back link */}
      <button
        onClick={() => navigate('/billing')}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Billing
      </button>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        Create Invoice from Unbilled Items
      </h1>

      {/* Error banner */}
      {unbilled.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{unbilled.error}</p>
        </div>
      )}

      {/* Matter & Period section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Matter picker */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client / Matter
            </label>
            {matterId && !showPicker ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
                <div className="flex-1 min-w-0">
                  {clientLabel && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{clientLabel}</div>
                  )}
                  <div className="text-gray-900 dark:text-white truncate">{matterLabel}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  className="text-xs text-lex-accent hover:underline flex-shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <ClientMatterPicker
                onSelect={handleMatterSelect}
                onCancel={() => { if (matterId) setShowPicker(false); }}
                variant="light"
              />
            )}
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Period Start
            </label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => handlePeriodChange('start', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Period End
            </label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => handlePeriodChange('end', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description (optional)
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Invoice description or notes..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
          />
        </div>
      </div>

      {/* Items section */}
      {matterId && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {unbilled.loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading unbilled items...</span>
            </div>
          ) : !hasItems ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No unbilled items found for this matter.</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Try adjusting the date range or check that time entries/expenses exist.
              </p>
            </div>
          ) : (
            <>
              {/* Tab pills */}
              <div className="flex items-center gap-2 px-4 pt-4 pb-2">
                <button
                  onClick={() => setItemTab('time-entries')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    itemTab === 'time-entries'
                      ? 'bg-lex-accent-soft text-lex-accent'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  Time Entries ({unbilled.timeEntries.length})
                </button>
                <button
                  onClick={() => setItemTab('expenses')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    itemTab === 'expenses'
                      ? 'bg-lex-accent-soft text-lex-accent'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  Expenses ({unbilled.expenses.length})
                </button>
              </div>

              {/* Select all bar */}
              <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-sm">
                {itemTab === 'time-entries' ? (
                  <>
                    <span className="text-gray-500 dark:text-gray-400">
                      {selectedTimeEntryIds.size} of {unbilled.timeEntries.length} selected
                    </span>
                    <button
                      onClick={allTimeSelected ? deselectAllTimeEntries : selectAllTimeEntries}
                      className="text-lex-accent hover:underline text-xs"
                    >
                      {allTimeSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-gray-500 dark:text-gray-400">
                      {selectedExpenseIds.size} of {unbilled.expenses.length} selected
                    </span>
                    <button
                      onClick={allExpSelected ? deselectAllExpenses : selectAllExpenses}
                      className="text-lex-accent hover:underline text-xs"
                    >
                      {allExpSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </>
                )}
              </div>

              {/* Item table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">
                      <th className="px-2 py-2 text-left w-8"></th>
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">Description</th>
                      {itemTab === 'expenses' && <th className="px-2 py-2 text-left">Category</th>}
                      <th className="px-2 py-2 text-right">Orig {itemTab === 'time-entries' ? 'Hrs' : 'Qty'}</th>
                      <th className="px-2 py-2 text-center">Billed {itemTab === 'time-entries' ? 'Hrs' : 'Qty'}</th>
                      <th className="px-2 py-2 text-right">Orig Rate</th>
                      <th className="px-2 py-2 text-center">Billed Rate</th>
                      <th className="px-2 py-2 text-right">Amount</th>
                      <th className="px-2 py-2 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {itemTab === 'time-entries' ? (
                      unbilled.timeEntries.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500 text-sm">
                            No unbilled time entries for this matter.
                          </td>
                        </tr>
                      ) : (
                        unbilled.timeEntries.map((te) => (
                          <BillableItemRow
                            key={te.time_entry_id}
                            item={te}
                            variant="time-entry"
                            isSelected={selectedTimeEntryIds.has(te.time_entry_id)}
                            onToggle={toggleTimeEntry}
                            override={overrides.get(te.time_entry_id) || null}
                            onOverrideChange={handleOverrideChange}
                          />
                        ))
                      )
                    ) : (
                      unbilled.expenses.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500 text-sm">
                            No unbilled expenses for this matter.
                          </td>
                        </tr>
                      ) : (
                        unbilled.expenses.map((exp) => (
                          <BillableItemRow
                            key={exp.expense_id}
                            item={exp}
                            variant="expense"
                            isSelected={selectedExpenseIds.has(exp.expense_id)}
                            onToggle={toggleExpense}
                            override={overrides.get(exp.expense_id) || null}
                            onOverrideChange={handleOverrideChange}
                          />
                        ))
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals bar */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <InvoiceTotalsBar
                  timeEntries={unbilled.timeEntries}
                  expenses={unbilled.expenses}
                  selectedTimeEntryIds={selectedTimeEntryIds}
                  selectedExpenseIds={selectedExpenseIds}
                  overrides={overrides}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={() => navigate('/billing')}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !matterId || !hasSelection}
          className="px-6 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          {submitting ? 'Creating...' : 'Create Draft Invoice'}
        </button>
      </div>
    </div>
  );
};

export default CreateInvoicePage;
